import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ExternalLink,
  Globe2,
  MapPin,
  Map as MapIcon,
  Navigation,
  Wifi,
  Hash,
  ShieldAlert,
  X,
  Copy,
  Check,
} from "lucide-react";
import "./IpAuditModal.css";

const ipAuditCache = new Map();

function isFiniteNumber(value) {
  const num = Number(value);
  return Number.isFinite(num);
}

function formatLocation(data) {
  const city = String(data?.city || "").trim();
  const region = String(data?.region || "").trim();
  const country = String(data?.country || "").trim();
  const postal = String(data?.postal || "").trim();

  const lineOne = [city, region].filter(Boolean).join(", ") || country || "Unknown location";
  const lineTwo = [country, postal].filter(Boolean).join(" - ") || "Location metadata unavailable";

  return { lineOne, lineTwo };
}

const SECURITY_FLAGS = ["anonymous", "proxy", "vpn", "tor", "hosting", "compromised", "scraper"];
const DANGEROUS_FLAGS = new Set(["vpn", "tor", "proxy", "compromised", "anonymous"]);

export default function IpAuditModal({ ip, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopyIp = () => {
    navigator.clipboard.writeText(ip).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !ip) {
      setData(null);
      setError("");
      setLoading(false);
      return;
    }

    const normalizedIp = String(ip).trim();
    if (!normalizedIp) return;

    if (ipAuditCache.has(normalizedIp)) {
      setData(ipAuditCache.get(normalizedIp));
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetch(`https://ip.aikit.club/?ip=${encodeURIComponent(normalizedIp)}`)
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.error || payload?.message || "Failed to load IP details");
        }
        if (!payload || typeof payload !== "object") {
          throw new Error("Invalid IP audit response");
        }
        if (!cancelled) {
          ipAuditCache.set(normalizedIp, payload);
          setData(payload);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Failed to load IP details");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ip, open]);

  const location = useMemo(() => formatLocation(data), [data]);
  const coordinates = useMemo(() => {
    if (!isFiniteNumber(data?.latitude) || !isFiniteNumber(data?.longitude)) return "";
    return `${Number(data.latitude)}, ${Number(data.longitude)}`;
  }, [data]);
  const mapHref = coordinates
    ? `https://www.google.com/maps?q=${encodeURIComponent(coordinates)}`
    : "";
  const ispLabel = data?.connection?.isp || data?.connection?.org || "—";
  const asnLabel = data?.connection?.asn ? `AS${data.connection.asn}` : "";

  const activeFlags = useMemo(() => {
    if (!data?.security) return [];
    return SECURITY_FLAGS.filter((flag) => Boolean(data.security[flag]));
  }, [data]);

  const overlayRef = useRef(null);
  const mouseDownOnOverlay = useRef(false);

  if (!open) return null;

  const handleOverlayMouseDown = (e) => {
    mouseDownOnOverlay.current = e.target === overlayRef.current;
  };

  const handleOverlayMouseUp = (e) => {
    if (mouseDownOnOverlay.current && e.target === overlayRef.current) {
      onClose();
    }
    mouseDownOnOverlay.current = false;
  };

  const modal = (
    <div
      className="ip-audit-overlay"
      ref={overlayRef}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      role="presentation"
    >
      <div
        className="ip-audit-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ip-audit-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ip-audit-header">
          <div className="ip-audit-header-left">
            <span className="ip-audit-header-icon">
              <Globe2 size={18} />
            </span>
            <div>
              <div className="ip-audit-ip-row">
                <h3 id="ip-audit-title" className="ip-audit-ip">
                  {ip}
                </h3>
                <button
                  type="button"
                  className={`ip-audit-copy ${copied ? "ip-audit-copy-done" : ""}`}
                  onClick={handleCopyIp}
                  aria-label="Copy IP"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                </button>
              </div>
              <div className="ip-audit-header-tags">
                {data?.type && <span className="ip-audit-tag ip-audit-tag-type">{data.type}</span>}
                {data?.country && (
                  <span className="ip-audit-tag ip-audit-tag-country">
                    {data.flag ? `${data.flag} ` : ""}
                    {data.country}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" className="ip-audit-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="ip-audit-state">
            <div className="ip-audit-spinner" aria-hidden="true" />
            <span>Loading IP details…</span>
          </div>
        ) : error ? (
          <div className="ip-audit-state ip-audit-state-error">{error}</div>
        ) : data ? (
          <div className="ip-audit-grid">
            <div className="ip-audit-row">
              <span className="ip-audit-label">
                <MapPin size={14} className="ip-audit-row-icon" />
                Location
              </span>
              <span className="ip-audit-value">{location.lineOne}</span>
            </div>
            <div className="ip-audit-row">
              <span className="ip-audit-label">
                <MapIcon size={14} className="ip-audit-row-icon" />
                Region
              </span>
              <span className="ip-audit-value">{location.lineTwo}</span>
            </div>
            {coordinates && (
              <div className="ip-audit-row">
                <span className="ip-audit-label">
                  <Navigation size={14} className="ip-audit-row-icon" />
                  Coordinates
                </span>
                <span className="ip-audit-value">
                  {mapHref ? (
                    <a
                      className="ip-audit-coord-link"
                      href={mapHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {coordinates} <ExternalLink size={12} />
                    </a>
                  ) : (
                    coordinates
                  )}
                </span>
              </div>
            )}
            <div className="ip-audit-row">
              <span className="ip-audit-label">
                <Wifi size={14} className="ip-audit-row-icon" />
                ISP
              </span>
              <span className="ip-audit-value">{ispLabel}</span>
            </div>
            {asnLabel && (
              <div className="ip-audit-row">
                <span className="ip-audit-label">
                  <Hash size={14} className="ip-audit-row-icon" />
                  ASN
                </span>
                <span className="ip-audit-value">{asnLabel}</span>
              </div>
            )}
            {activeFlags.length > 0 && (
              <div className="ip-audit-row ip-audit-row-flags">
                <span className="ip-audit-label">
                  <ShieldAlert size={14} className="ip-audit-row-icon ip-audit-row-icon-warn" />
                  Security
                </span>
                <span className="ip-audit-value">
                  <span className="ip-audit-flags-wrap">
                    {activeFlags.map((flag) => (
                      <span
                        key={flag}
                        className={`admin-badge ${DANGEROUS_FLAGS.has(flag) ? "admin-badge-danger" : "admin-badge-success"}`}
                      >
                        {flag.charAt(0).toUpperCase() + flag.slice(1)}
                      </span>
                    ))}
                  </span>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="ip-audit-state">No IP details available.</div>
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
